function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

export function buildNotificationFieldValueMap(responseFields = []) {
  const values = {
    caller_name: 'Test Caller',
    caller_email: 'test@example.com',
    caller_number: '+919845004028',
  }

  ;(responseFields || []).forEach((row) => {
    const path = String(row?.path ?? row?.fullPath ?? '').trim()
    if (!path) return
    values[path] = String(row?.value ?? '').trim()
  })

  return values
}

export function resolveNotificationTemplate(template, responseFields = []) {
  if (!template) return ''
  const values = buildNotificationFieldValueMap(responseFields)

  return String(template).replace(/\{([a-zA-Z0-9_.]+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key) && values[key] !== '') {
      return values[key]
    }
    return match
  })
}

export function extractTemplateFieldPaths(...templates) {
  const paths = new Set()

  templates.forEach((template) => {
    String(template || '').replace(/\{([a-zA-Z0-9_.]+)\}/g, (_, key) => {
      if (key) paths.add(key)
    })
  })

  return [...paths]
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function templateToEditableHtml(template, responseFields = []) {
  if (!template) return ''

  const values = buildNotificationFieldValueMap(responseFields)

  return String(template).replace(/\{([a-zA-Z0-9_.]+)\}/g, (match, key) => {
    const display = values[key] || key
    return placeholderHtml(match, display)
  })
}

function placeholderPathFromToken(token = '') {
  return String(token).replace(/^\{|\}$/g, '').trim()
}

const PLACEHOLDER_CHIP_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'gap:5px',
  'max-width:100%',
  'padding:4px 6px 4px 8px',
  'margin:2px 4px',
  'border-radius:999px',
  'background:#047857',
  'border:1px solid #065f46',
  'box-shadow:0 1px 2px rgba(15,23,42,0.18)',
  'color:#ffffff',
  'font-size:12px',
  'font-weight:600',
  'line-height:1.3',
  'white-space:nowrap',
  'vertical-align:middle',
  'box-sizing:border-box',
  'font-family:ui-sans-serif,system-ui,-apple-system,sans-serif',
].join(';')

const PLACEHOLDER_CHIP_PATH_STYLE = [
  'display:inline-block',
  'padding:1px 6px',
  'border-radius:999px',
  'background:rgba(255,255,255,0.18)',
  'color:#ecfdf5',
  'font-size:10px',
  'font-weight:700',
  'letter-spacing:0.03em',
  'text-transform:lowercase',
  'font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace',
].join(';')

const PLACEHOLDER_CHIP_LABEL_STYLE = [
  'overflow:hidden',
  'text-overflow:ellipsis',
  'max-width:200px',
  'color:#ffffff',
  'font-size:12px',
  'font-weight:700',
].join(';')

const PLACEHOLDER_CHIP_REMOVE_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'justify-content:center',
  'width:18px',
  'height:18px',
  'padding:0',
  'margin:0',
  'border:none',
  'border-radius:999px',
  'background:rgba(255,255,255,0.18)',
  'color:#ffffff',
  'font-size:14px',
  'font-weight:700',
  'line-height:1',
  'cursor:pointer',
  'flex-shrink:0',
].join(';')

function applyPlaceholderChipStyles(span, pathEl, label, removeBtn) {
  span.style.cssText = PLACEHOLDER_CHIP_STYLE
  pathEl.style.cssText = PLACEHOLDER_CHIP_PATH_STYLE
  label.style.cssText = PLACEHOLDER_CHIP_LABEL_STYLE
  removeBtn.style.cssText = PLACEHOLDER_CHIP_REMOVE_STYLE
}

export function placeholderHtml(token, display) {
  const safeDisplay = escapeHtml(display)
  const safeToken = escapeHtml(token)
  const safePath = escapeHtml(placeholderPathFromToken(token))
  return (
    `<span class="ocn-ph" contenteditable="false" data-token="${safeToken}" `
    + `style="${PLACEHOLDER_CHIP_STYLE}">`
    + `<span class="ocn-ph__path" style="${PLACEHOLDER_CHIP_PATH_STYLE}">${safePath}</span>`
    + `<span class="ocn-ph__label" style="${PLACEHOLDER_CHIP_LABEL_STYLE}">${safeDisplay}</span>`
    + '<button type="button" class="ocn-ph__remove" data-action="remove-ph" '
    + `style="${PLACEHOLDER_CHIP_REMOVE_STYLE}" `
    + 'contenteditable="false" aria-label="Remove field" title="Remove field">'
    + '&times;</button>'
    + '</span>'
  )
}

function appendProcessedNode(parent, node, processed) {
  if (processed == null || processed === '') return

  if (typeof processed === 'string') {
    parent.appendChild(document.createTextNode(processed))
    return
  }

  parent.appendChild(processed)
}

function processEditableNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || ''
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }

  const element = node

  if (element.classList?.contains('ocn-ph') && element.dataset.token) {
    return element.dataset.token
  }

  if (element.classList?.contains('ocn-ph__remove')
    || element.classList?.contains('ocn-ph__label')
    || element.classList?.contains('ocn-ph__path')) {
    return ''
  }

  const clone = element.cloneNode(false)
  element.childNodes.forEach((child) => {
    appendProcessedNode(clone, child, processEditableNode(child))
  })
  return clone
}

export function editableHtmlToTemplate(html) {
  if (!html) return ''

  const container = document.createElement('div')
  container.innerHTML = html

  let result = ''
  container.childNodes.forEach((child) => {
    const processed = processEditableNode(child)
    if (typeof processed === 'string') {
      result += processed
    } else {
      result += processed.outerHTML
    }
  })

  return result
}

export function extractEmailStyleBlock(template) {
  const match = String(template || '').match(/<style[^>]*>[\s\S]*?<\/style>/i)
  return match ? match[0] : ''
}

export function stripEmailStyleBlocks(html) {
  return String(html || '').replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
}

export function splitEmailTemplate(template) {
  const source = String(template || '')
  const match = source.match(/^(?<start>[\s\S]*<body[^>]*>)(?<body>[\s\S]*?)(?<end><\/body>[\s\S]*)$/i)

  if (!match?.groups) {
    return { shellStart: '', body: source, shellEnd: '' }
  }

  return {
    shellStart: match.groups.start,
    body: match.groups.body,
    shellEnd: match.groups.end,
  }
}

export function mergeEmailTemplate(shellStart, body, shellEnd) {
  if (!shellStart && !shellEnd) return body
  return `${shellStart}${body}${shellEnd}`
}

function isNodeInsideEditor(editor, node) {
  if (!editor || !node) return false
  const element = node.nodeType === Node.TEXT_NODE ? node.parentNode : node
  return Boolean(element && editor.contains(element))
}

export function saveCaretRange(editor) {
  const selection = window.getSelection()
  if (!editor || !selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!isNodeInsideEditor(editor, range.commonAncestorContainer)) return null

  return range.cloneRange()
}

export function createRangeAtEditorEnd(editor) {
  const range = document.createRange()
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest('style')) return NodeFilter.FILTER_REJECT
      return node.textContent?.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    },
  })

  let lastText = null
  while (walker.nextNode()) {
    lastText = walker.currentNode
  }

  if (lastText) {
    range.setStart(lastText, lastText.textContent.length)
    range.collapse(true)
    return range
  }

  const contentNodes = [...editor.childNodes].filter(
    (node) => !(node.nodeType === Node.ELEMENT_NODE && node.tagName === 'STYLE'),
  )
  const target = contentNodes[contentNodes.length - 1] || editor
  range.selectNodeContents(target)
  range.collapse(false)
  return range
}

export function resolveInsertRange(editor, savedRange) {
  if (savedRange) {
    try {
      if (isNodeInsideEditor(editor, savedRange.commonAncestorContainer)) {
        return savedRange.cloneRange()
      }
    } catch {
      // Saved range is no longer valid.
    }
  }

  const selection = window.getSelection()
  if (selection?.rangeCount && isNodeInsideEditor(editor, selection.anchorNode)) {
    return selection.getRangeAt(0).cloneRange()
  }

  return createRangeAtEditorEnd(editor)
}

function getStyleReferenceElement(range) {
  const node = range.startContainer
  if (node.nodeType === Node.TEXT_NODE) return node.parentElement
  if (node.nodeType === Node.ELEMENT_NODE) return node
  return null
}

function applyInlineStylesFromReference(target, referenceEl) {
  if (!target || !referenceEl) return

  const computed = window.getComputedStyle(referenceEl)
  target.style.fontFamily = computed.fontFamily
  target.style.fontSize = computed.fontSize
  target.style.fontWeight = computed.fontWeight
  target.style.fontStyle = computed.fontStyle
  target.style.lineHeight = computed.lineHeight
  target.style.color = computed.color
  target.style.letterSpacing = computed.letterSpacing
}

export function createPlaceholderSpan(token, display, range) {
  const span = document.createElement('span')
  span.className = 'ocn-ph'
  span.contentEditable = 'false'
  span.dataset.token = token

  const pathEl = document.createElement('span')
  pathEl.className = 'ocn-ph__path'
  pathEl.textContent = placeholderPathFromToken(token)

  const label = document.createElement('span')
  label.className = 'ocn-ph__label'
  label.textContent = display

  const removeBtn = document.createElement('button')
  removeBtn.type = 'button'
  removeBtn.className = 'ocn-ph__remove'
  removeBtn.dataset.action = 'remove-ph'
  removeBtn.contentEditable = 'false'
  removeBtn.setAttribute('aria-label', 'Remove field')
  removeBtn.title = 'Remove field'
  removeBtn.textContent = '×'

  span.append(pathEl, label, removeBtn)
  applyPlaceholderChipStyles(span, pathEl, label, removeBtn)
  return span
}

function insertSpacingAfter(node) {
  const space = document.createTextNode(' ')
  node.parentNode?.insertBefore(space, node.nextSibling)
  return space
}

export function insertPlaceholderAtCaret(editor, savedRange, token, display) {
  if (!editor) return null

  editor.focus()
  const range = resolveInsertRange(editor, savedRange)
  range.deleteContents()

  const span = createPlaceholderSpan(token, display, range)
  range.insertNode(span)
  const space = insertSpacingAfter(span)

  const after = document.createRange()
  after.setStart(space, space.textContent?.length || 1)
  after.collapse(true)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(after)

  return after.cloneRange()
}

export function insertNewlineAtCaret(editor, savedRange) {
  if (!editor) return null

  editor.focus()
  const range = resolveInsertRange(editor, savedRange)
  range.deleteContents()

  const br = document.createElement('br')
  range.insertNode(br)

  const space = document.createTextNode('\u00a0')
  br.parentNode?.insertBefore(space, br.nextSibling)

  const after = document.createRange()
  after.setStart(space, 0)
  after.collapse(true)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(after)

  return after.cloneRange()
}

export function insertEmailDetailRowAtCaret(editor, savedRange, label = 'HOST Name:') {
  if (!editor) return insertNewlineAtCaret(editor, savedRange)

  editor.focus()
  resolveInsertRange(editor, savedRange)

  let node = window.getSelection()?.anchorNode
  if (node?.nodeType === Node.TEXT_NODE) node = node.parentNode

  const currentRow = node?.closest?.('tr')
  const tbody = node?.closest?.('tbody') || editor.querySelector('tbody')

  const tr = document.createElement('tr')

  const labelCell = document.createElement('td')
  labelCell.style.padding = '10px 0'
  labelCell.style.fontWeight = '600'
  labelCell.style.width = '40%'
  labelCell.style.verticalAlign = 'top'
  labelCell.textContent = label

  const valueCell = document.createElement('td')
  valueCell.style.padding = '10px 0'
  valueCell.style.verticalAlign = 'top'
  valueCell.appendChild(document.createTextNode('\u00a0'))

  tr.append(labelCell, valueCell)

  if (currentRow?.parentNode) {
    currentRow.parentNode.insertBefore(tr, currentRow.nextSibling)
  } else if (tbody) {
    tbody.appendChild(tr)
  } else {
    return insertNewlineAtCaret(editor, savedRange)
  }

  const after = document.createRange()
  after.selectNodeContents(valueCell)
  after.collapse(true)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(after)

  return after.cloneRange()
}

function findPlaceholderSibling(range, direction = 'before') {
  const { startContainer, startOffset } = range
  if (!range.collapsed) return null

  if (startContainer.nodeType === Node.TEXT_NODE) {
    const text = startContainer.textContent || ''
    if (direction === 'before' && startOffset === 0) {
      let sibling = startContainer.previousSibling
      while (sibling?.nodeType === Node.TEXT_NODE && !sibling.textContent?.trim()) {
        sibling = sibling.previousSibling
      }
      return sibling?.nodeType === Node.ELEMENT_NODE && sibling.classList?.contains('ocn-ph')
        ? sibling
        : null
    }
    if (direction === 'after' && startOffset >= text.length) {
      let sibling = startContainer.nextSibling
      while (sibling?.nodeType === Node.TEXT_NODE && !sibling.textContent?.trim()) {
        sibling = sibling.nextSibling
      }
      return sibling?.nodeType === Node.ELEMENT_NODE && sibling.classList?.contains('ocn-ph')
        ? sibling
        : null
    }
    return null
  }

  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const element = startContainer
    const index = direction === 'before' ? startOffset - 1 : startOffset
    const candidate = element.childNodes[index]
    if (candidate?.nodeType === Node.ELEMENT_NODE && candidate.classList?.contains('ocn-ph')) {
      return candidate
    }
  }

  return null
}

export function removePlaceholderAtCaret(editor, range, direction = 'before') {
  const placeholder = findPlaceholderSibling(range, direction)
  if (!placeholder) return false

  const trailing = placeholder.nextSibling
  if (trailing?.nodeType === Node.TEXT_NODE && trailing.textContent === ' ') {
    trailing.remove()
  }

  placeholder.remove()
  return true
}

export function handleTemplateEditorKeydown(event, editor) {
  if (!editor || (event.key !== 'Backspace' && event.key !== 'Delete')) return false

  const selection = window.getSelection()
  if (!selection?.rangeCount) return false

  const range = selection.getRangeAt(0)
  if (!range.collapsed) return false

  const direction = event.key === 'Backspace' ? 'before' : 'after'
  if (!removePlaceholderAtCaret(editor, range, direction)) return false

  event.preventDefault()
  return true
}

export function removePlaceholderFromClick(target) {
  const button = target?.closest?.('[data-action="remove-ph"]')
  if (!button) return null
  return button.closest('.ocn-ph')
}

export function extractNotificationTemplatesFromTest(raw) {
  const data = raw?.data && typeof raw.data === 'object' ? raw.data : {}
  const top = raw && typeof raw === 'object' ? raw : {}

  return {
    email_subject:
      pickString(data, 'email_subject', 'emailSubject')
      || pickString(top, 'email_subject', 'emailSubject'),
    email_template:
      pickString(data, 'email_template', 'emailTemplate')
      || pickString(top, 'email_template', 'emailTemplate'),
    whatsapp_template:
      pickString(data, 'whatsapp_template', 'whatsappTemplate')
      || pickString(top, 'whatsapp_template', 'whatsappTemplate'),
  }
}
